import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import * as Toast from "@radix-ui/react-toast";
import { ArrowRight, Layers3, ShieldCheck, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useLogin } from "../reducers";
import useRecordingControls from "../utils/useRecordingControls";
import { useSettings } from "../utils/SettingsContext";
import { useActionItems } from "./dashboard/ActionItemsContext";
import { pendingReviewHref } from "./dashboard/pendingReviewHref";
import { unanalyzedSessionsHref } from "./dashboard/unanalyzedSessionsHref";

/** Transient invitations to review or analyze, without taking focus from the current page. */
export default function RecordingReadyToasts() {
  const { t } = useTranslation();
  const { user } = useLogin();
  const { recordingReviewReady, pendingReviewCount } = useRecordingControls();
  const { analysisReady, unanalyzed } = useActionItems();
  const { autoAnalyze } = useSettings();
  const [dismissed, setDismissed] = useState({ review: 0, analysis: 0 });
  useEffect(() => {
    if (analysisReady && (autoAnalyze || unanalyzed === 0)) {
      setDismissed(current => ({ ...current, analysis: analysisReady.id }));
    }
  }, [autoAnalyze, unanalyzed, analysisReady]);
  const notices = [
    {
      kind: "review" as const,
      notice: recordingReviewReady,
      visible: pendingReviewCount > 0,
      title: "review_ready_title" as const,
      description: "dashboard.pending_review_description" as const,
      action: "dashboard.pending_review_action" as const,
      to: pendingReviewHref(user.id),
      icon: ShieldCheck,
    },
    {
      kind: "analysis" as const,
      notice: analysisReady,
      visible: unanalyzed > 0 && !autoAnalyze,
      title: "analysis_ready_title" as const,
      description: "analysis_ready_description" as const,
      action: "analysis_ready_action" as const,
      to: unanalyzedSessionsHref(user.id),
      icon: Layers3,
    },
  ];
  if (!recordingReviewReady && !analysisReady) return null;

  return (
    <Toast.Provider duration={12000} swipeDirection="right">
      {notices.map(({ kind, notice, visible, title, description, action, to, icon: Icon }) => {
        if (!notice) return null;
        const dismiss = () => setDismissed(current => ({ ...current, [kind]: notice.id }));
        return (
          <Toast.Root
            key={`${kind}-${notice.id}`}
            open={notice.id > dismissed[kind] && visible}
            onOpenChange={open => {
              if (!open) dismiss();
            }}
            className="relative flex items-start gap-3 rounded-xl border border-info/25 bg-surface p-4 text-text shadow-xl"
          >
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-info" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <Toast.Title className="text-sm font-semibold">
                {t(title, { count: notice.count })}
              </Toast.Title>
              <Toast.Description className="mt-1 text-sm leading-5 text-text-mid">
                {t(description)}
              </Toast.Description>
              <Toast.Action asChild altText={t(action)}>
                <Link
                  to={to}
                  onClick={dismiss}
                  className="mt-3 inline-flex min-h-9 items-center gap-2 rounded-lg bg-info px-3 py-2 text-sm font-medium text-white transition hover:bg-info-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
                >
                  {t(action)}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </Toast.Action>
            </div>
            <Toast.Close
              aria-label={t("close")}
              className="-mr-1 -mt-1 rounded-lg p-2 text-text-dim transition hover:bg-muted-surface hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-info"
            >
              <X size={16} aria-hidden="true" />
            </Toast.Close>
          </Toast.Root>
        );
      })}
      {createPortal(
        <Toast.Viewport
          label={t("activity.to_do")}
          className="fixed bottom-6 right-4 z-[100] flex w-[min(420px,calc(100vw-32px))] max-w-full flex-col gap-2 outline-none sm:right-6"
        />,
        document.body,
      )}
    </Toast.Provider>
  );
}
